<script lang="ts" setup>
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, useAttrs, useCssModule, useSlots, watch } from 'vue';

  import { getOwnerDomContext } from '../../utils/helpers';
  import type { FieldProps } from '../Field/Field.types';
  import Field from '../Field/Field.vue';

  export interface TextareaResizeOptions {
    /**
     * It will automatically scroll the page down when it reaches the bottom of the viewport/element
     */
    forceBrowserScroll: boolean;
  }

  export interface TextAreaProps extends FieldProps {
    /**
     * Value for the textarea element.
     */
    modelValue?: string;

    /**
     * Deprecated. Use :model-value or v-model instead of :value.
     * @deprecated Use :model-value or v-model instead of :value.
     */
    value?: string | number | null;

    /**
     * Allow textarea to be resizable vertically.
     * Alternatively if you want to disable automatic scroll when resizing, you can set `{ forceBrowserScroll: false }`
     */
    resize?: boolean | TextareaResizeOptions;

    /**
     * Placeholder text for the textarea element.
     * **Note:** placeholders should be used to display examples; they should not be used as labels because they are not accessible as labels. If a real label cannot be used, use the `aria-label` attribute.
     */
    placeholder?: string;

    /**
     * Number of rows to display in the textarea.
     */
    rows?: number;

    /**
     * Maximum number of characters allowed in the textarea.
     */
    maxlength?: number;
  }

  defineOptions({
    name: 'll-textarea',
  });

  const attrs = useAttrs();
  const slots = useSlots();
  const classes = useCssModule();

  const props = withDefaults(defineProps<TextAreaProps>(), {
    modelValue: '',
    value: null,
    resize: false,
    maxlength: undefined,
    rows: undefined,
    placeholder: undefined,
  });

  const emits = defineEmits<{
    /**
     * Emitted when the model value changes.
     */
    (e: 'update:model-value', value: string): void;
  }>();

  const textareaRef = ref<HTMLTextAreaElement>();
  const observer = ref<ResizeObserver>();
  const isReadOnly = computed(() => props.isReadOnly || ('readonly' in attrs && attrs.readonly !== false));

  const inputAttrs = computed(() => {
    const allAttrs = { ...attrs };

    delete allAttrs['data-test'];
    delete allAttrs.class;

    return allAttrs;
  });

  watch(
    () => props.resize,
    (v) => {
      if (v) {
        setupResizeObserver();
      } else {
        observer.value?.disconnect();
      }
    },
  );

  const onInput = (event: Event) => {
    emits('update:model-value', (event.target as HTMLTextAreaElement).value);
  };

  const setupResizeObserver = () => {
    if (observer.value || !textareaRef.value) {
      return;
    }

    // the ResizeObserver will be in charge to detect if page needs to scroll when resizing the component
    observer.value = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (!entry) {
        return;
      }

      const target = entry.target as HTMLElement;
      const { ownerDoc, ownerWin } = getOwnerDomContext(target);
      const parent = findParentScrollable(target) || ownerDoc.documentElement;

      const { scrollTop: scrollPosition } = parent;
      let offsetDiff = 0;

      // checks if the closest parent element scrollable is the document page
      if (parent === ownerDoc.documentElement) {
        const { top, height } = getOffsetClipRect(target);
        const { innerHeight: viewportHeight } = ownerWin;

        offsetDiff = Math.max(top + height - (viewportHeight + scrollPosition), 0);
      } else {
        const { top, height } = target.getBoundingClientRect();
        const { top: parentTop } = parent.getBoundingClientRect();
        const { offsetHeight: parentHeight } = parent;
        const offsetTop = top - parentTop;

        offsetDiff = Math.max(offsetTop + height - parentHeight, 0);
      }

      if (offsetDiff) {
        requestAnimationFrame(() => {
          parent.scrollTop = scrollPosition + offsetDiff;
        });
      }
    });

    observer.value.observe(textareaRef.value);
  };

  /**
   * Retrieve the closest parent that has a scroll. Defaults to the document page.
   */
  const findParentScrollable = (el: HTMLElement): HTMLElement | null => {
    const { ownerDoc, ownerWin } = getOwnerDomContext(el);
    const parent = el.parentElement as HTMLElement;
    if (!parent) {
      return null;
    }

    const { overflowY } = ownerWin.getComputedStyle(parent);
    if (overflowY !== 'visible') {
      return parent;
    }

    if (parent === ownerDoc.body) {
      return ownerDoc.documentElement;
    }

    return findParentScrollable(parent);
  };

  /**
   * Retrieve element absolute positioning relative to the page.
   */
  const getOffsetClipRect = (el: HTMLElement) => {
    const { offsetWidth: width, offsetHeight: height } = el;

    let left = 0;
    let top = 0;

    const findPos = function ({ offsetLeft, offsetTop, offsetParent }: HTMLElement) {
      left += offsetLeft;
      top += offsetTop;

      if (offsetParent) {
        findPos(offsetParent as HTMLElement);
      }
    };

    findPos(el);

    return {
      top,
      left,
      width,
      height,
    };
  };

  onMounted(async () => {
    if (props.value !== null) {
      throw new Error('ll-input: use :model-value or v-model instead of :value.');
    }

    if (attrs.onInput) {
      throw new Error('ll-input: use the @update:model-value event instead of @input');
    }

    if (
      (typeof props.resize === 'boolean' && props.resize) ||
      (props.resize as TextareaResizeOptions)?.forceBrowserScroll
    ) {
      await nextTick();
      setupResizeObserver();
    }
  });

  onBeforeUnmount(() => {
    observer.value?.disconnect();
  });
</script>

<template>
  <Field v-bind="props" class="stash-textarea" :class="[classes.root]" data-test="stash-textarea">
    <template #default="{ fieldId, fieldErrorId, hasError }">
      <textarea
        :id="fieldId"
        ref="textareaRef"
        :aria-errormessage="fieldErrorId"
        :aria-invalid="hasError"
        :class="[
          classes.textarea,
          'border border-ice-500',
          {
            'stash-textarea--error border-red-500 text-red-500': hasError,
            'text-ice-700 hover:border-ice-500 focus:border-blue-500 active:border-blue-500':
              !hasError && !props.disabled,
            'resize-y': props.resize,
            'min-h-[100px]': !props.rows,
            'resize-none': !props.resize,
          },
        ]"
        :maxlength="props.maxlength"
        :value="props.modelValue"
        data-test="stash-textarea|textarea"
        :placeholder="props.placeholder"
        v-bind="inputAttrs"
        :disabled="props.disabled"
        :readonly="isReadOnly"
        :rows="props.rows"
        @input="onInput"
      ></textarea>
    </template>
    <template v-if="slots.hint" #hint>
      <!-- @slot Hint content -->
      <slot name="hint"></slot>
    </template>
  </Field>
</template>

<style module>
  @reference "../../../styles/main.css";

  .root {
    position: relative;
    width: 100%;
  }

  .textarea {
    background: var(--color-white);
    border-radius: var(--radius-sm);
    display: block;
    outline: none;
    padding: --spacing(1.5);
    width: 100%;

    &::placeholder {
      color: var(--color-ice-500);
      opacity: 1;
    }

    &[disabled] {
      background-color: var(--color-ice-100);
      border-color: var(--color-ice-500);
      color: var(--color-ice-500);
      pointer-events: none;
    }

    &[disabled]:active,
    &[disabled]:focus {
      box-shadow: none;
    }

    &[disabled]::placeholder {
      text-transform: none;
      color: var(--color-ice-500);
    }

    &[readonly] {
      border-color: transparent;
      background-color: transparent;
      padding-left: 0;
      padding-right: 0;
      min-height: unset;
    }
  }
</style>
